import React, { useState, useEffect } from 'react';
import { AppIcon, documentCategoryIcon, documentShareIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
}

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  yearsActive: string;
}

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  category: 'flyer' | 'lesson_plan' | 'medical' | 'form' | 'announcement' | 'policy' | 'other';
  shareType: 'all_families' | 'specific_families' | 'admin_only' | 'all_alumni' | 'specific_alumni' | 'families_and_alumni';
  specificFamilyIds: string;
  specificAlumniIds?: string;
  downloadCount: number;
  expiresAt: string | null;
  createdAt: string;
  uploader: {
    firstName: string;
    lastName: string;
  };
}

interface UploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
  families: Family[];
  alumni: AlumniMember[];
}

function UploadModal({ onClose, onSuccess, families, alumni }: UploadModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    shareType: 'admin_only',
    specificFamilyIds: [] as string[],
    specificAlumniIds: [] as string[],
    expiresAt: ''
  });
  const [hasExpiration, setHasExpiration] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors(['File size must be less than 10MB']);
        return;
      }
      setSelectedFile(file);
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    if (!selectedFile) {
      setErrors(['Please select a file to upload']);
      setLoading(false);
      return;
    }

    try {
      // First upload the file
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);

      const token = localStorage.getItem('authToken');
      const uploadResponse = await fetch('/api/upload/document', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (!uploadResponse.ok) {
        const uploadError = await uploadResponse.json();
        throw new Error(uploadError.message || 'File upload failed');
      }

      const uploadData = await uploadResponse.json();
      const fileInfo = uploadData.data;

      // Then create the document record
      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          expiresAt: hasExpiration ? formData.expiresAt : null,
          specificFamilyIds: formData.shareType === 'specific_families' ? formData.specificFamilyIds : null,
          specificAlumniIds: formData.shareType === 'specific_alumni' ? formData.specificAlumniIds : null,
          fileName: fileInfo.fileName,
          originalFileName: fileInfo.originalFileName,
          fileSize: fileInfo.fileSize,
          mimeType: fileInfo.mimeType,
          filePath: fileInfo.filePath
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        console.error('Document upload error:', data);
        const errorMessages = data.errors?.map((err: any) => `${err.path}: ${err.msg}`) || [data.message];
        setErrors(errorMessages);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setErrors(['Failed to upload document. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Upload Document</h3>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  required
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max file size: 10MB. Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, TXT, XLSX, XLS
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="e.g., Fall 2024 Lesson Plan"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="Optional description of the document..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="flyer">Flyer</option>
                  <option value="lesson_plan">Lesson Plan</option>
                  <option value="medical">Medical</option>
                  <option value="form">Form</option>
                  <option value="announcement">Announcement</option>
                  <option value="policy">Policy</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Share With</label>
                <select
                  value={formData.shareType}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    shareType: e.target.value as any,
                    specificFamilyIds: e.target.value === 'specific_families' ? prev.specificFamilyIds : [],
                    specificAlumniIds: e.target.value === 'specific_alumni' ? prev.specificAlumniIds : []
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="admin_only">Admin Only</option>
                  <option value="all_families">All Families</option>
                  <option value="specific_families">Specific Families</option>
                  <option value="all_alumni">All Alumni</option>
                  <option value="specific_alumni">Specific Alumni</option>
                  <option value="families_and_alumni">Families and Alumni</option>
                </select>
              </div>

              {formData.shareType === 'specific_families' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Families</label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {families.map(family => (
                      <label key={family.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.specificFamilyIds.includes(family.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                specificFamilyIds: [...prev.specificFamilyIds, family.id]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                specificFamilyIds: prev.specificFamilyIds.filter(id => id !== family.id)
                              }));
                            }
                          }}
                          className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded mr-2"
                        />
                        <span className="text-sm">
                          {family.familyName} ({family.primaryContactFirstName} {family.primaryContactLastName})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.shareType === 'specific_alumni' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Alumni</label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {alumni.map(alumniMember => (
                      <label key={alumniMember.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={formData.specificAlumniIds.includes(alumniMember.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                specificAlumniIds: [...prev.specificAlumniIds, alumniMember.id]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                specificAlumniIds: prev.specificAlumniIds.filter(id => id !== alumniMember.id)
                              }));
                            }
                          }}
                          className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded mr-2"
                        />
                        <span className="text-sm">
                          {alumniMember.firstName} {alumniMember.lastName}
                          {alumniMember.yearsActive && <span className="text-gray-500"> ({alumniMember.yearsActive})</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={hasExpiration}
                    onChange={(e) => {
                      setHasExpiration(e.target.checked);
                      if (!e.target.checked) {
                        setFormData(prev => ({ ...prev, expiresAt: '' }));
                      }
                    }}
                    className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded mr-2"
                  />
                  Set Document Expiration
                </label>
                {hasExpiration && (
                  <div className="mt-2">
                    <input
                      type="datetime-local"
                      value={formData.expiresAt}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Document will automatically become unavailable after this date
                    </p>
                  </div>
                )}
                {!hasExpiration && (
                  <p className="text-xs text-gray-500 mt-1">
                    Document will remain available indefinitely
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedFile || !formData.title}
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

interface EditDocumentModalProps {
  document: Document;
  onClose: () => void;
  onSuccess: () => void;
  families: Family[];
  alumni: AlumniMember[];
}

function EditDocumentModal({ document, onClose, onSuccess, families, alumni }: EditDocumentModalProps) {
  const [formData, setFormData] = useState({
    title: document.title,
    description: document.description || '',
    category: document.category,
    shareType: document.shareType,
    specificFamilyIds: document.specificFamilyIds ? JSON.parse(document.specificFamilyIds) : [] as string[],
    specificAlumniIds: document.specificAlumniIds ? JSON.parse(document.specificAlumniIds) : [] as string[],
    expiresAt: document.expiresAt ? new Date(document.expiresAt).toISOString().slice(0, 16) : ''
  });
  const [hasExpiration, setHasExpiration] = useState(!!document.expiresAt);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [familySearchTerm, setFamilySearchTerm] = useState('');
  const [alumniSearchTerm, setAlumniSearchTerm] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/documents/${document.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          expiresAt: hasExpiration ? formData.expiresAt : null,
          specificFamilyIds: formData.shareType === 'specific_families' ? formData.specificFamilyIds : null,
          specificAlumniIds: formData.shareType === 'specific_alumni' ? formData.specificAlumniIds : null
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        console.error('Document update error:', data);
        const errorMessages = data.errors?.map((err: any) => `${err.path}: ${err.msg}`) || [data.message];
        setErrors(errorMessages);
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setErrors(['Failed to update document. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const toggleFamily = (familyId: string) => {
    setFormData(prev => ({
      ...prev,
      specificFamilyIds: prev.specificFamilyIds.includes(familyId)
        ? prev.specificFamilyIds.filter(id => id !== familyId)
        : [...prev.specificFamilyIds, familyId]
    }));
  };

  const toggleAlumni = (alumniId: string) => {
    setFormData(prev => ({
      ...prev,
      specificAlumniIds: prev.specificAlumniIds.includes(alumniId)
        ? prev.specificAlumniIds.filter(id => id !== alumniId)
        : [...prev.specificAlumniIds, alumniId]
    }));
  };

  const selectAllFilteredFamilies = () => {
    const filtered = filteredFamilies.map(f => f.id);
    setFormData(prev => ({
      ...prev,
      specificFamilyIds: [...new Set([...prev.specificFamilyIds, ...filtered])]
    }));
  };

  const deselectAllFilteredFamilies = () => {
    const filtered = filteredFamilies.map(f => f.id);
    setFormData(prev => ({
      ...prev,
      specificFamilyIds: prev.specificFamilyIds.filter(id => !filtered.includes(id))
    }));
  };

  const selectAllFilteredAlumni = () => {
    const filtered = filteredAlumni.map(a => a.id);
    setFormData(prev => ({
      ...prev,
      specificAlumniIds: [...new Set([...prev.specificAlumniIds, ...filtered])]
    }));
  };

  const deselectAllFilteredAlumni = () => {
    const filtered = filteredAlumni.map(a => a.id);
    setFormData(prev => ({
      ...prev,
      specificAlumniIds: prev.specificAlumniIds.filter(id => !filtered.includes(id))
    }));
  };

  const filteredFamilies = families.filter(family =>
    family.familyName.toLowerCase().includes(familySearchTerm.toLowerCase()) ||
    `${family.primaryContactFirstName} ${family.primaryContactLastName}`.toLowerCase().includes(familySearchTerm.toLowerCase())
  );

  const filteredAlumni = alumni.filter(alumniMember =>
    `${alumniMember.firstName} ${alumniMember.lastName}`.toLowerCase().includes(alumniSearchTerm.toLowerCase()) ||
    (alumniMember.email && alumniMember.email.toLowerCase().includes(alumniSearchTerm.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[90vh]">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Edit Document</h3>
                <p className="text-sm text-gray-600 mt-1">{document.originalFileName}</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="e.g., Fall 2024 Lesson Plan"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  placeholder="Optional description of the document..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="flyer">Flyer</option>
                  <option value="lesson_plan">Lesson Plan</option>
                  <option value="medical">Medical</option>
                  <option value="form">Form</option>
                  <option value="announcement">Announcement</option>
                  <option value="policy">Policy</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Share With</label>
                <select
                  value={formData.shareType}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    shareType: e.target.value as any
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="admin_only">Admin Only</option>
                  <option value="all_families">All Families</option>
                  <option value="specific_families">Specific Families</option>
                  <option value="all_alumni">All Alumni</option>
                  <option value="specific_alumni">Specific Alumni</option>
                  <option value="families_and_alumni">Families and Alumni</option>
                </select>
              </div>

              {/* Family Selection */}
              {formData.shareType === 'specific_families' && (
                <div className="md:col-span-2">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Families ({formData.specificFamilyIds.length} selected)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllFilteredFamilies}
                        className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        type="button"
                        onClick={deselectAllFilteredFamilies}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search families..."
                    value={familySearchTerm}
                    onChange={(e) => setFamilySearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  />
                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                    {filteredFamilies.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No families found</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredFamilies.map(family => (
                          <label key={family.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.specificFamilyIds.includes(family.id)}
                              onChange={() => toggleFamily(family.id)}
                              className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                            />
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">{family.familyName}</span>
                              <span className="text-sm text-gray-500 ml-2">
                                ({family.primaryContactFirstName} {family.primaryContactLastName})
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Alumni Selection */}
              {formData.shareType === 'specific_alumni' && (
                <div className="md:col-span-2">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Select Alumni ({formData.specificAlumniIds.length} selected)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllFilteredAlumni}
                        className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-gray-400">|</span>
                      <button
                        type="button"
                        onClick={deselectAllFilteredAlumni}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Search alumni..."
                    value={alumniSearchTerm}
                    onChange={(e) => setAlumniSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  />
                  <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                    {filteredAlumni.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No alumni found</div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredAlumni.map(alumniMember => (
                          <label key={alumniMember.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.specificAlumniIds.includes(alumniMember.id)}
                              onChange={() => toggleAlumni(alumniMember.id)}
                              className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                            />
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">
                                {alumniMember.firstName} {alumniMember.lastName}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                {alumniMember.email}
                                {alumniMember.yearsActive && ` (${alumniMember.yearsActive})`}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={hasExpiration}
                    onChange={(e) => {
                      setHasExpiration(e.target.checked);
                      if (!e.target.checked) {
                        setFormData(prev => ({ ...prev, expiresAt: '' }));
                      }
                    }}
                    className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded mr-2"
                  />
                  Set Document Expiration
                </label>
                {hasExpiration && (
                  <div className="mt-2">
                    <input
                      type="datetime-local"
                      value={formData.expiresAt}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Document will automatically become unavailable after this date
                    </p>
                  </div>
                )}
                {!hasExpiration && (
                  <p className="text-xs text-gray-500 mt-1">
                    Document will remain available indefinitely
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-white flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || 
                !formData.title ||
                (formData.shareType === 'specific_families' && formData.specificFamilyIds.length === 0) ||
                (formData.shareType === 'specific_alumni' && formData.specificAlumniIds.length === 0)
              }
              className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DocumentManagement() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [alumni, setAlumni] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [uploadDateFilter, setUploadDateFilter] = useState<string>('all');
  const [shareWithFilter, setShareWithFilter] = useState<string>('all');

  useEffect(() => {
    fetchDocuments();
    fetchFamilies();
    fetchAlumni();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/documents', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAlumni(data.data);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
    }
  };

  const handleDeleteDocument = async (documentId: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        alert('Document deleted successfully!');
        fetchDocuments();
      } else {
        alert('Failed to delete document: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate unique months from document upload dates
  const getAvailableUploadMonths = () => {
    const months = new Set<string>();
    documents.forEach(doc => {
      const uploadDate = new Date(doc.createdAt);
      const monthKey = uploadDate.getFullYear() + '-' + String(uploadDate.getMonth() + 1).padStart(2, '0');
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse(); // Most recent first
  };

  const formatMonthDisplay = (monthKey: string) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatShareType = (shareType: string) => {
    switch (shareType) {
      case 'all_families': return 'All Families';
      case 'specific_families': return 'Specific Families';
      case 'admin_only': return 'Admin Only';
      default: return shareType;
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // Category filter
    if (filter !== 'all' && doc.category !== filter) return false;
    
    // Search filter (title and original file name)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const titleMatch = doc.title.toLowerCase().includes(searchLower);
      const fileNameMatch = doc.originalFileName.toLowerCase().includes(searchLower);
      if (!titleMatch && !fileNameMatch) return false;
    }
    
    // Upload date filter
    if (uploadDateFilter !== 'all') {
      const uploadDate = new Date(doc.createdAt);
      const filterMonth = uploadDate.getFullYear() + '-' + String(uploadDate.getMonth() + 1).padStart(2, '0');
      if (filterMonth !== uploadDateFilter) return false;
    }
    
    // Share with filter
    if (shareWithFilter !== 'all' && doc.shareType !== shareWithFilter) return false;
    
    return true;
  });

  const stats = {
    total: documents.length,
    categories: {
      flyer: documents.filter(d => d.category === 'flyer').length,
      lesson_plan: documents.filter(d => d.category === 'lesson_plan').length,
      medical: documents.filter(d => d.category === 'medical').length,
      form: documents.filter(d => d.category === 'form').length,
      announcement: documents.filter(d => d.category === 'announcement').length,
      policy: documents.filter(d => d.category === 'policy').length,
      other: documents.filter(d => d.category === 'other').length
    },
    shareTypes: {
      admin_only: documents.filter(d => d.shareType === 'admin_only').length,
      shared: documents.filter(d => d.shareType === 'all_families' || d.shareType === 'specific_families').length
    },
    totalDownloads: documents.reduce((sum, doc) => sum + doc.downloadCount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
        <span className="ml-2 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="px-12 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Document Management</h1>
          <p className="text-gray-600 mt-2">Upload and share documents with families</p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-ggaa-green text-white px-6 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium"
        >
          Upload Document
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search documents by title or filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-ggaa-green focus:border-ggaa-green"
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Documents</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-blue-600">{stats.totalDownloads}</div>
          <div className="text-sm text-gray-600">Total Downloads</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-green-600">{stats.shareTypes.admin_only}</div>
          <div className="text-sm text-gray-600">Admin Only Documents</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-2xl font-bold text-purple-600">{stats.shareTypes.shared}</div>
          <div className="text-sm text-gray-600">Shared Documents</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        
        {/* Category Filter */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-ggaa-green text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total})
            </button>
            {Object.entries(stats.categories).map(([category, count]) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === category 
                    ? 'bg-ggaa-green text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <AppIcon name={documentCategoryIcon(category)} className="w-4 h-4" />
                  {category.replace('_', ' ').charAt(0).toUpperCase() + category.replace('_', ' ').slice(1)} ({count})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Date and Share With Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Upload Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Date</label>
            <select
              value={uploadDateFilter}
              onChange={(e) => setUploadDateFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
            >
              <option value="all">All Months</option>
              {getAvailableUploadMonths().map(month => (
                <option key={month} value={month}>
                  {formatMonthDisplay(month)}
                </option>
              ))}
            </select>
          </div>

          {/* Share With Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Shared With</label>
            <select
              value={shareWithFilter}
              onChange={(e) => setShareWithFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
            >
              <option value="all">All Types</option>
              <option value="admin_only">Admin Only</option>
              <option value="all_families">All Families</option>
              <option value="specific_families">Specific Families</option>
              <option value="all_alumni">All Alumni</option>
              <option value="specific_alumni">Specific Alumni</option>
              <option value="families_and_alumni">Families and Alumni</option>
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(filter !== 'all' || searchTerm || uploadDateFilter !== 'all' || shareWithFilter !== 'all') && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setFilter('all');
                setSearchTerm('');
                setUploadDateFilter('all');
                setShareWithFilter('all');
              }}
              className="text-sm text-ggaa-green hover:text-ggaa-green/80 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="py-6 bg-white rounded-xl shadow-lg">
          <EmptyState
            icon="emptyDocs"
            title="No documents found"
            description={
              filter === 'all' && !searchTerm && uploadDateFilter === 'all' && shareWithFilter === 'all'
                ? "You haven't uploaded any documents yet."
                : "No documents match your current filters. Try adjusting your search or filter criteria."
            }
          />
          <div className="text-center mb-6">
          {filter === 'all' && !searchTerm && uploadDateFilter === 'all' && shareWithFilter === 'all' ? (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-ggaa-green text-white px-6 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors"
            >
              Upload Your First Document
            </button>
          ) : (
            <button
              onClick={() => {
                setFilter('all');
                setSearchTerm('');
                setUploadDateFilter('all');
                setShareWithFilter('all');
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear All Filters
            </button>
          )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shared With</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Downloads</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.map(document => (
                  <tr key={document.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <AppIcon name={documentCategoryIcon(document.category)} className="w-5 h-5 mr-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-md" title={document.title}>
                            {document.title}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-md" title={document.originalFileName}>
                            {document.originalFileName}
                          </div>
                          {document.description && (
                            <div className="text-xs text-gray-400 mt-1 line-clamp-2 max-w-md" title={document.description}>
                              {document.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {document.category.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <AppIcon name={documentShareIcon(document.shareType)} className="w-4 h-4 mr-1 inline-block" />
                        <span className="text-sm text-gray-900">
                          {document.shareType === 'all_families' ? 'All Families' :
                           document.shareType === 'specific_families' ? 'Specific Families' :
                           document.shareType === 'all_alumni' ? 'All Alumni' :
                           document.shareType === 'specific_alumni' ? 'Specific Alumni' :
                           document.shareType === 'families_and_alumni' ? 'Families & Alumni' :
                           'Admin Only'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(document.fileSize)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {document.downloadCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(document.createdAt)}</div>
                      <div className="text-sm text-gray-500">by {document.uploader.firstName} {document.uploader.lastName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingDocument(document)}
                          className="text-green-600 hover:text-green-900 font-medium"
                        >
                          Edit
                        </button>
                        <a
                          href={`/api/documents/${document.id}/download`}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          Download
                        </a>
                        <button 
                          onClick={() => handleDeleteDocument(document.id, document.title)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          families={families}
          alumni={alumni}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchDocuments();
          }}
        />
      )}

      {/* Edit Document Modal */}
      {editingDocument && (
        <EditDocumentModal
          document={editingDocument}
          families={families}
          alumni={alumni}
          onClose={() => setEditingDocument(null)}
          onSuccess={() => {
            setEditingDocument(null);
            fetchDocuments();
          }}
        />
      )}
    </div>
  );
} 